import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    HiOutlineArrowLeft,
    HiOutlineCalendar,
    HiOutlineChevronDown,
    HiOutlineChevronLeft,
    HiOutlineChevronRight,
    HiOutlineCurrencyDollar,
    HiOutlineRefresh,
    HiOutlineUsers,
} from "react-icons/hi";
import { ClipboardCopyIcon, CheckIcon } from "@heroicons/react/outline";
import Card from "../components/shared/Card";
import Badge from "../components/shared/Badge";
import Button from "../components/shared/Button";
import { getGroupTransactions } from "../redux/api/analyticsAPI";
import { dateRangeToBounds, formatRevenue } from "../utils/revenueAnalytics";

const PAGE_SIZE = 25;
const DATE_RANGES = [
    "Last 7 Days",
    "Last 30 Days",
    "Last 90 Days",
    "This Year",
    "All Time",
];

const GroupDetails = () => {
    const { groupId } = useParams();
    const organisationId = useSelector(
        (state) => state.organisation?.currentOrganisation?._id
    );

    const [dateRange, setDateRange] = useState("Last 30 Days");
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [page, setPage] = useState(1);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [group, setGroup] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const [copiedId, setCopiedId] = useState(false);

    const { startDate, endDate } = useMemo(
        () => dateRangeToBounds(dateRange),
        [dateRange]
    );

    useEffect(() => {
        setPage(1);
    }, [dateRange]);

    useEffect(() => {
        if (!organisationId || !groupId) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        getGroupTransactions({
            organisationId,
            groupId,
            startDate,
            endDate,
            page,
            pageSize: PAGE_SIZE,
        }).then((res) => {
            if (cancelled) return;
            if (res.error) {
                setError(res.error);
                setRows([]);
                setTotal(0);
            } else {
                setRows(res.data?.rows || []);
                setTotal(res.data?.total || 0);
                setGroup(res.data?.group || null);
            }
            setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [organisationId, groupId, startDate, endDate, page, reloadKey]);

    const copyId = () => {
        navigator.clipboard.writeText(groupId);
        setCopiedId(true);
        setTimeout(() => setCopiedId(false), 2000);
    };

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const rangeEnd = Math.min(page * PAGE_SIZE, total);

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    return (
        <div className="analytics-container min-h-screen p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
                    <div className="flex items-center gap-3">
                        <Link
                            to="/members/groups"
                            className="inline-flex items-center gap-1 text-body-md text-link hover:text-link-active"
                            data-help-title="Back to groups"
                            data-help="Returns to the Manage Groups page."
                        >
                            <HiOutlineArrowLeft className="h-4 w-4" />
                            Groups
                        </Link>
                        <span className="text-muted">/</span>
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-md bg-surface-soft">
                                <HiOutlineUsers className="h-6 w-6 text-ink" />
                            </div>
                            <div>
                                <h1 className="text-display-md text-ink" data-help-title="Group details" data-help="Shows this group's summary — members, points balance, reward share, and total revenue — plus every qualifying transaction in the selected date range.">
                                    {group?.name || "Group Details"}
                                </h1>
                                <div className="flex items-center gap-1 mt-0.5">
                                    <span className="text-caption text-muted font-mono">
                                        {groupId}
                                    </span>
                                    <button
                                        onClick={copyId}
                                        className="p-0.5 rounded-sm text-muted hover:text-ink transition-colors"
                                        title="Copy Group ID"
                                        data-help-title="Copy group ID"
                                        data-help="Copies the group's full ID to the clipboard — useful when configuring integrations that need to reference this group."
                                    >
                                        {copiedId
                                            ? <CheckIcon className="h-3.5 w-3.5 text-signature-forest" />
                                            : <ClipboardCopyIcon className="h-3.5 w-3.5" />
                                        }
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <Button
                            variant="icon-circular"
                            onClick={() => setReloadKey((k) => k + 1)}
                            aria-label="Refresh data"
                            data-help-title="Refresh"
                            data-help="Pulls the latest data for this group without changing any of your filters. The icon spins while loading."
                        >
                            <HiOutlineRefresh
                                className={`h-5 w-5 ${loading ? "animate-spin" : ""}`}
                            />
                        </Button>

                        <div className="relative">
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setShowDatePicker((v) => !v)}
                                leadingIcon={<HiOutlineCalendar className="h-4 w-4" />}
                                trailingIcon={<HiOutlineChevronDown className="h-4 w-4" />}
                                data-help-title="Date range"
                                data-help="Opens the date picker to switch between Last 7 Days, Last 30 Days, Last 90 Days, This Year, or All Time. Every figure on the page updates to match."
                            >
                                {dateRange}
                            </Button>
                            <div
                                className={`absolute right-0 mt-2 w-48 rounded-md shadow-sm z-10 border border-hairline bg-canvas dropdown-menu ${
                                    showDatePicker ? "active" : ""
                                }`}
                            >
                                <div className="p-2">
                                    {DATE_RANGES.map((range) => (
                                        <button
                                            key={range}
                                            onClick={() => {
                                                setDateRange(range);
                                                setShowDatePicker(false);
                                            }}
                                            data-help-title="Date range option"
                                            data-help="Applies this reporting period to all figures on the page and returns to the first page of transactions."
                                            className={`block w-full text-left px-4 py-2 text-body-md rounded-sm ${
                                                dateRange === range
                                                    ? "bg-surface-soft text-ink"
                                                    : "text-body"
                                            }`}
                                        >
                                            {range}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Summary stats */}
                {group && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                        <Card variant="default" className="flex flex-col items-center py-4" data-help-title="Members" data-help="Shows how many members are currently in this group.">
                            <div className="text-display-md text-ink font-medium">
                                {group.membersCount ?? 0}
                            </div>
                            <div className="text-caption text-muted uppercase tracking-wide mt-1">
                                Members
                            </div>
                        </Card>
                        <Card variant="surface-soft" className="flex flex-col items-center py-4" data-help-title="Points balance" data-help="Shows the group's current points balance — the points the group has received from its share of members' earned rewards.">
                            <div className="text-display-md text-ink font-medium">
                                {(group.points ?? 0).toLocaleString()}
                            </div>
                            <div className="text-caption text-muted uppercase tracking-wide mt-1">
                                Points Balance
                            </div>
                        </Card>
                        <Card variant="default" className="flex flex-col items-center py-4" data-help-title="Reward share" data-help="Shows the percentage of each member's earned points that goes to the group's pool; the remainder stays with the individual member.">
                            <div className="text-display-md text-ink font-medium">
                                {(group.rewardDistribution != null
                                    ? Math.round(group.rewardDistribution * 100)
                                    : 0) + "%"}
                            </div>
                            <div className="text-caption text-muted uppercase tracking-wide mt-1">
                                Reward Share
                            </div>
                        </Card>
                        <Card variant="cream-callout" className="flex flex-col items-center py-4" data-help-title="Total revenue" data-help="Shows the total revenue generated by this group inside the selected date range.">
                            <div className="text-display-md text-ink font-medium flex items-center gap-1">
                                <HiOutlineCurrencyDollar className="h-5 w-5" />
                                {formatRevenue(group.totalRevenue)}
                            </div>
                            <div className="text-caption text-muted uppercase tracking-wide mt-1">
                                Total Revenue
                            </div>
                        </Card>
                    </div>
                )}

                {/* Transactions table */}
                <Card variant="default" className="!p-0 overflow-hidden">
                    <div className="p-5 border-b border-hairline flex items-center justify-between gap-4 flex-wrap">
                        <h2 className="text-title-md text-ink flex items-center gap-2">
                            <HiOutlineCurrencyDollar className="h-5 w-5 text-ink" />
                            <span>Transactions</span>
                        </h2>
                        <Badge variant="default">
                            {total.toLocaleString()} total
                        </Badge>
                    </div>

                    <div className="px-5 py-3 bg-surface-soft border-b border-hairline">
                        <div className="flex text-caption text-muted uppercase tracking-wide">
                            <div className="w-1/5 px-2">Date</div>
                            <div className="w-1/5 px-2">Member</div>
                            <div className="w-1/5 px-2">Integration</div>
                            <div className="w-1/5 px-2 text-right">Revenue</div>
                            <div className="w-1/5 px-2 text-right">Group Share (pts)</div>
                        </div>
                    </div>

                    {loading ? (
                        <div className="px-5 py-12 text-center text-body-md text-muted">
                            Loading…
                        </div>
                    ) : error ? (
                        <div className="px-5 py-12 text-center text-body-md text-signature-coral">
                            {error}
                        </div>
                    ) : rows.length === 0 ? (
                        <div className="px-5 py-12 text-center text-body-md text-muted">
                            No transactions in this range.
                        </div>
                    ) : (
                        <ul>
                            {rows.map((row, idx) => (
                                <li
                                    key={idx}
                                    className="border-b border-hairline px-5 py-4"
                                    data-help-title="Transaction row"
                                    data-help="A qualifying activity in the selected period — the date, the member, which integration recorded it, the revenue, and the points the group received from it."
                                >
                                    <div className="flex items-center">
                                        <div className="w-1/5 px-2 text-body-md text-muted">
                                            {formatDate(row.submittedAt)}
                                        </div>
                                        <div className="w-1/5 px-2">
                                            <div className="text-body-md font-medium text-ink">
                                                {row.memberName || "No Name"}
                                            </div>
                                            <div
                                                className="text-body-md mt-0.5 truncate max-w-[160px] text-muted"
                                                title={row.memberIdentification}
                                            >
                                                {row.memberIdentification}
                                            </div>
                                        </div>
                                        <div className="w-1/5 px-2 text-body-md text-body">
                                            {row.integrationName || "—"}
                                        </div>
                                        <div className="w-1/5 px-2 text-right text-body-md font-medium text-ink">
                                            {formatRevenue(row.revenue)}
                                        </div>
                                        <div className="w-1/5 px-2 text-right text-body-md text-body">
                                            {(row.groupShare ?? 0).toLocaleString()}
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}

                    {total > 0 && (
                        <div className="px-5 py-4 flex items-center justify-between bg-surface-soft">
                            <span className="text-body-md text-muted">
                                {rangeStart}–{rangeEnd} of {total.toLocaleString()}
                            </span>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    disabled={page <= 1 || loading}
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    leadingIcon={
                                        <HiOutlineChevronLeft className="h-4 w-4" />
                                    }
                                    data-help-title="Previous page"
                                    data-help="Shows the previous 25 transactions. Disabled on the first page."
                                >
                                    Prev
                                </Button>
                                <span className="text-body-md text-muted">
                                    Page {page} of {totalPages}
                                </span>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    disabled={page >= totalPages || loading}
                                    onClick={() =>
                                        setPage((p) => Math.min(totalPages, p + 1))
                                    }
                                    trailingIcon={
                                        <HiOutlineChevronRight className="h-4 w-4" />
                                    }
                                    data-help-title="Next page"
                                    data-help="Shows the next 25 transactions. Disabled on the last page."
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default GroupDetails;
